import React, { useEffect, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../common/Button';
import { makeApiRequest } from '../../api/apiService';
import { AuthContext } from '../../context/AuthContext';

const ReportScreen = () => {
  const [step, setStep] = useState(1);
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const [selectedReport, setSelectedReport] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const reportOptions = [
    { label: 'Account Summary', value: 'account-summary' },
    { label: 'Open Positions', value: 'open-positions' },
    { label: 'Statement', value: 'statement' },
    { label: 'Deposit & Withdrawal (Combined)', value: 'deposit-withdrawal' },
  ];

  const formatOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Excel', value: 'excel' },
    { label: 'CSV', value: 'csv' },
  ];

  const getExportEndpoint = (reportValue, format, startDateParam = '', endDateParam = '') => {
    // Build date query params if provided
    const dateParams = [];
    if (startDateParam) dateParams.push(`startDate=${encodeURIComponent(startDateParam)}`);
    if (endDateParam) dateParams.push(`endDate=${encodeURIComponent(endDateParam)}`);
    const queryString = dateParams.length > 0 ? `&${dateParams.join('&')}` : '';

    switch (reportValue) {
      case 'account-summary':
        return `/trading/reports/trader/account-summary/export?format=${encodeURIComponent(format)}${queryString}`;
      case 'open-positions':
        return `/trading/reports/trader/open-positions/export?format=${encodeURIComponent(format)}${queryString}`;
      case 'statement':
        return `/trading/reports/trader/statement/export?format=${encodeURIComponent(format)}${queryString}`;
      // case 'deposit-withdrawal': {
      //   // Expected endpoints:
      //   // GET /api/reports/deposit-withdrawal/export/pdf
      //   // GET /api/reports/deposit-withdrawal/export/excel
      //   const fmt = format?.toLowerCase();
      //   if (fmt === 'pdf') return `/reports/deposit-withdrawal/export/pdf${queryString ? '?' + dateParams.join('&') : ''}`;
      //   if (fmt === 'excel') return `/reports/deposit-withdrawal/export/excel${queryString ? '?' + dateParams.join('&') : ''}`;
      //   return null; // unsupported for this report
      // }
      default:
        return null;
    }
  };

  useEffect(() => {
    // Reset wizard state whenever user navigates to this page again
    // (ensures it feels "fresh" on every sidebar click)
    setStep(1);
    setSelectedReport('');
    setSelectedFormat('pdf');
    setStartDate('');
    setEndDate('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  
  const handleNext = () => {
    if (!selectedReport) {
      alert('Please select a report.');
      return;
    }
    setStep(2);
  };

  const handleExport = async () => {
    if (!user?.access_token) {
      alert('User is not authenticated.');
      return;
    }

    if (!selectedReport) {
      alert('Please select a report.');
      return;
    }

    const format = selectedFormat?.toLowerCase();
    const allowedFormats =
      selectedReport === 'deposit-withdrawal' ? ['pdf', 'excel'] : ['pdf', 'excel', 'csv'];

    if (!format || !allowedFormats.includes(format)) {
      alert(
        selectedReport === 'deposit-withdrawal'
          ? 'Please select a valid export format (pdf, excel).'
          : 'Please select a valid export format (pdf, excel, csv).'
      );
      return;
    }

    const endpoint = getExportEndpoint(selectedReport, format, startDate, endDate);
    if (!endpoint) {
      setIsLoading(false);
      setError(
        selectedReport === 'deposit-withdrawal'
          ? 'Deposit & Withdrawal (Combined) supports only PDF and Excel.'
          : 'Invalid report/export format selection.'
      );
      return;
    }

    setIsLoading(true);
    setError('');

    const response = await makeApiRequest(endpoint, user.access_token, 'GET', null);

    setIsLoading(false);

    if (response.error) {
      setError(response.message || 'Failed to export report.');
      return;
    }

    // New combined-report contract:
    // { signedUrl, expiresIn, ... }
    // Older reports used:
    // { downloadUrl }
    const signedUrl = response.data?.signedUrl;
    const downloadUrl = response.data?.downloadUrl;

    const urlToOpen = signedUrl || downloadUrl;
    if (!urlToOpen) {
      setError('Export succeeded but neither signedUrl nor downloadUrl is present in the response.');
      return;
    }

    window.open(urlToOpen, '_blank');
  };

  const handleCreate = () => {
    if (!selectedReport) {
      alert('Please select a report.');
      return;
    }
    handleExport();
  };

  return (
    <div className="p-5">
      <div className="w-full bg-white p-6 rounded-lg shadow-md">
        {step === 1 ? (
          <>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Please select a report</h2>
            <select
              className="w-full p-2 pr-10 border rounded mb-4 focus:ring-2 focus:ring-gray-400 appearance-none bg-white"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234b5563' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.7rem center',
                backgroundSize: '1.4rem',
              }}
              value={selectedReport}
              onChange={(e) => setSelectedReport(e.target.value)}
              aria-label="Select report type"
            >
              <option value=""></option>
              {reportOptions.map((report) => (
                <option key={report.value} value={report.value}>
                  {report.label}
                </option>
              ))}
            </select>

            <div className="w-full flex justify-center">
              <Button width="w-64" onClick={handleNext}>
                Next
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-bold mb-4 text-gray-800">Export Details</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (Optional)</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded mb-4 focus:ring-2 focus:ring-gray-400"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="Start date"
                />
                <p className="text-xs text-gray-500 mb-4">Leave empty to get all records</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date (Optional)</label>
                <input
                  type="date"
                  className="w-full p-2 border rounded mb-4 focus:ring-2 focus:ring-gray-400"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="End date"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                <select
                  className="w-full p-2 pr-10 border rounded mb-4 focus:ring-2 focus:ring-gray-400 appearance-none bg-white"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234b5563' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 0.7rem center',
                    backgroundSize: '1.4rem',
                  }}
                  value={selectedFormat}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  aria-label="Select export format"
                >
                  {formatOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {isLoading ? (
                <p>Exporting...</p>
              ) : error ? (
                <p className="text-red-500">{error}</p>
              ) : null}
            </div>

            <div className="flex justify-between mt-4">
              <Button width="w-32" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button width="w-32" onClick={handleCreate}>
                Create
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportScreen;
